import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { dienstName, protokollName } from '../lib/datenverkehr'
import { formatBitsPerSecond, formatBytes } from '../lib/format'
import type { FlowReport, FlowTop } from '../lib/types'
import { LineChart } from './LineChart'

const ZEITRAEUME: [string, string, number][] = [
  ['1h', '1 Stunde', 3_600_000],
  ['24h', '24 Stunden', 24 * 3_600_000],
  ['7d', '7 Tage', 7 * 24 * 3_600_000],
  ['30d', '30 Tage', 30 * 24 * 3_600_000],
]

interface Props {
  deviceId: string
  /** Aufbewahrung des Kunden in Tagen; 0 heißt „nicht sammeln“. */
  aufbewahrungTage: number
}

/**
 * Datenverkehr eines Geräts aus NetFlow, IPFIX oder sFlow: Menge über die
 * Zeit, die größten Quellen, Ziele, Dienste und Gespräche, je Interface.
 * Ohne Aufbewahrung beim Kunden und ohne Daten bleibt die Karte weg.
 */
export function DatenverkehrKarte({ deviceId, aufbewahrungTage }: Props) {
  const [zeitraum, setZeitraum] = useState('24h')
  const dauer = ZEITRAEUME.find(([k]) => k === zeitraum)?.[2] ?? 24 * 3_600_000

  const bericht = useQuery({
    queryKey: ['device-flows', deviceId, zeitraum],
    queryFn: ({ signal }) => {
      const bis = new Date()
      const von = new Date(bis.getTime() - dauer)
      const p = new URLSearchParams({ von: von.toISOString(), bis: bis.toISOString() })
      return apiGet<FlowReport>(`/devices/${deviceId}/datenverkehr?${p.toString()}`, signal)
    },
    refetchInterval: 60_000,
  })
  const d = bericht.data
  // Eine Antwort ohne die erwartete Form (etwa von einer älteren Zentrale)
  // lässt die Karte weg, statt die Geräteseite mitzureißen.
  if (!d || typeof d.bytes !== 'number' || !Array.isArray(d.verlauf)) return null
  if (d.bytes === 0 && aufbewahrungTage === 0) return null

  const punkte = d.verlauf.map((p) => ({
    time: new Date(p.zeit).getTime(),
    value: (p.bytes * 8) / d.schritt_s,
  }))
  const sekunden = (new Date(d.bis).getTime() - new Date(d.von).getTime()) / 1000

  return (
    <section className="card" aria-labelledby="datenverkehr-titel">
      <div className="card-head" style={{ flexWrap: 'wrap', gap: 12 }}>
        <h2 className="card-title" id="datenverkehr-titel">
          Datenverkehr
        </h2>
        <span className="muted" style={{ fontSize: 12 }}>
          {formatBytes(d.bytes)} · im Mittel {formatBitsPerSecond((d.bytes * 8) / Math.max(sekunden, 1))}
        </span>
        <div className="field" style={{ marginLeft: 'auto' }}>
          <label htmlFor="datenverkehr-zeitraum" className="sr-only">
            Zeitraum
          </label>
          <select
            id="datenverkehr-zeitraum"
            className="input"
            value={zeitraum}
            onChange={(e) => setZeitraum(e.target.value)}
          >
            {ZEITRAEUME.map(([k, text]) => (
              <option key={k} value={k}>
                {text}
              </option>
            ))}
          </select>
        </div>
      </div>

      {d.bytes === 0 ? (
        <div className="card-body muted">
          Noch kein Datenverkehr empfangen. Am Gerät NetFlow/IPFIX an den Agent auf UDP 2055 oder sFlow auf UDP 6343
          exportieren; am Agent muss der Empfang für Datenverkehr eingeschaltet sein.
        </div>
      ) : (
        <>
          <div className="card-body">
            <LineChart
              series={[{ key: 'flow', label: 'Datenverkehr', points: punkte }]}
              metric="flow_bits_per_s"
              minValue={0}
              height={180}
            />
            {d.dropped > 0 ? (
              <p style={{ color: 'var(--warn)', fontSize: 13 }}>
                Der Agent hat {d.dropped} Pakete wegen Überlast nicht gelesen; die Werte sind zu niedrig.
              </p>
            ) : null}
          </div>
          <div className="topliste-raster">
            <TopListe titel="Größte Quellen" zeilen={d.quellen} spalte={(t) => t.src ?? ''} gesamt={d.bytes} />
            <TopListe titel="Größte Ziele" zeilen={d.ziele} spalte={(t) => t.dst ?? ''} gesamt={d.bytes} />
            <TopListe
              titel="Dienste"
              zeilen={d.dienste}
              spalte={(t) => dienstName(t.proto, t.port)}
              gesamt={d.bytes}
              mono={false}
            />
          </div>
          <Gespraeche zeilen={d.gespraeche} />
          {d.interfaces.length > 0 ? (
            <table className="data" aria-label="Datenverkehr je Interface">
              <thead>
                <tr>
                  <th>Interface</th>
                  <th style={{ textAlign: 'right' }}>Hinein</th>
                  <th style={{ textAlign: 'right' }}>Hinaus</th>
                </tr>
              </thead>
              <tbody>
                {d.interfaces.map((i) => (
                  <tr key={i.if_index}>
                    <td>{i.name || `Index ${i.if_index}`}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {formatBytes(i.in_bytes)}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {formatBytes(i.out_bytes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      )}
    </section>
  )
}

function TopListe({
  titel,
  zeilen,
  spalte,
  gesamt,
  mono = true,
}: {
  titel: string
  zeilen: FlowTop[]
  spalte: (t: FlowTop) => string
  gesamt: number
  mono?: boolean
}) {
  return (
    <table className="data" aria-label={titel}>
      <thead>
        <tr>
          <th>{titel}</th>
          <th style={{ textAlign: 'right' }}>Menge</th>
          <th style={{ textAlign: 'right' }}>Anteil</th>
        </tr>
      </thead>
      <tbody>
        {zeilen.length === 0 ? (
          <tr>
            <td colSpan={3} className="muted">
              –
            </td>
          </tr>
        ) : (
          zeilen.map((t) => (
            <tr key={spalte(t)}>
              <td className={mono ? 'mono' : undefined} style={{ wordBreak: 'break-all' }}>
                {spalte(t)}
              </td>
              <td className="mono" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {formatBytes(t.bytes)}
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>
                {gesamt > 0 ? `${Math.round((t.bytes / gesamt) * 100)} %` : '–'}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function Gespraeche({ zeilen }: { zeilen: FlowTop[] }) {
  if (zeilen.length === 0) return null
  return (
    <table className="data" aria-label="Größte Gespräche">
      <thead>
        <tr>
          <th>Von</th>
          <th>Nach</th>
          <th>Dienst</th>
          <th style={{ textAlign: 'right' }}>Menge</th>
          <th style={{ textAlign: 'right' }}>Pakete</th>
        </tr>
      </thead>
      <tbody>
        {zeilen.map((t) => (
          <tr key={`${t.src}-${t.dst}-${t.proto}-${t.port}`}>
            <td className="mono">{t.src}</td>
            <td className="mono">{t.dst}</td>
            <td title={protokollName(t.proto)}>{dienstName(t.proto, t.port)}</td>
            <td className="mono" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              {formatBytes(t.bytes)}
            </td>
            <td className="mono" style={{ textAlign: 'right' }}>
              {t.packets.toLocaleString('de-AT')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
